<template>
    <v-dialog
        :model-value="modelValue"
        max-width="95vw"
        width="95vw"
        :fullscreen="$vuetify.display.mdAndDown"
        content-class="compare-dialog-content"
        @update:model-value="$emit('update:modelValue', $event)">
        <v-card
            class="compare-dialog-card"
            :class="{ 'compare-dialog-card--with-results': Boolean(result) }">
            <v-toolbar density="compact" color="surface" class="compare-dialog-toolbar">
                <v-toolbar-title>Compară liste de preț</v-toolbar-title>
                <v-spacer />
                <v-btn icon variant="text" @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="compare-dialog-body">
                <div class="compare-dialog-controls">
                <v-row class="align-center">
                    <v-col cols="12" md="5">
                        <v-select
                            v-model="leftId"
                            :items="sortedPriceLists"
                            :item-title="priceListItemTitle"
                            :item-props="leftItemProps"
                            item-value="id"
                            label="Listă de referință (veche)"
                            :variant="fieldVariant"
                            density="compact"
                            hide-details="auto"
                            clearable />
                    </v-col>
                    <v-col cols="12" md="5">
                        <v-select
                            v-model="rightId"
                            :items="sortedPriceLists"
                            :item-title="priceListItemTitle"
                            :item-props="rightItemProps"
                            item-value="id"
                            label="Listă de comparat (nouă)"
                            :variant="fieldVariant"
                            density="compact"
                            hide-details="auto"
                            clearable />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-btn
                            block
                            color="primary"
                            style="color: white !important"
                            :loading="loading"
                            :disabled="!canCompare"
                            @click="runCompare({ force: true })">
                            Compară
                        </v-btn>
                    </v-col>
                </v-row>

                <v-alert
                    v-if="error"
                    class="mt-4"
                    type="error"
                    variant="tonal"
                    density="compact">
                    {{ error }}
                </v-alert>

                <v-alert
                    v-if="missingSnapshotsWarning && !warningDismissed"
                    class="mt-4 compare-snapshot-warning"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    closable
                    close-label="Închide"
                    @click:close="warningDismissed = true">
                    {{ missingSnapshotsWarning }}
                </v-alert>

                <div
                    v-if="loading && !result"
                    class="d-flex flex-column align-center justify-center py-12">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                        size="48" />
                    <div class="text-body-2 text-medium-emphasis mt-4">
                        Se încarcă comparația…
                    </div>
                </div>
                </div>

                <div v-if="result" class="compare-results mt-4">
                    <div v-if="tableLoading" class="compare-results-overlay">
                        <v-progress-circular
                            indeterminate
                            color="primary"
                            size="40" />
                        <span class="text-body-2 text-medium-emphasis ml-3">
                            Se actualizează lista…
                        </span>
                    </div>

                    <div class="compare-results-toolbar">
                        <div class="d-flex flex-wrap ga-4 mb-4">
                            <v-chip
                                :color="statusFilter === 'added' ? 'success' : undefined"
                                :variant="statusFilter === 'added' ? 'flat' : 'tonal'"
                                :disabled="tableLoading"
                                @click="toggleStatusFilter('added')">
                                Produse noi: {{ result.summary.added }}
                            </v-chip>
                            <v-chip
                                :color="statusFilter === 'removed' ? 'error' : undefined"
                                :variant="statusFilter === 'removed' ? 'flat' : 'tonal'"
                                :disabled="tableLoading"
                                @click="toggleStatusFilter('removed')">
                                Eliminate: {{ result.summary.removed }}
                            </v-chip>
                            <v-chip
                                :color="statusFilter === 'changed' ? 'warning' : undefined"
                                :variant="statusFilter === 'changed' ? 'flat' : 'tonal'"
                                :disabled="tableLoading"
                                @click="toggleStatusFilter('changed')">
                                Modificate: {{ changedCount }}
                            </v-chip>
                            <v-chip
                                :color="kindFilter === 'new_furnizor' ? 'info' : undefined"
                                :variant="kindFilter === 'new_furnizor' ? 'flat' : 'tonal'"
                                :disabled="tableLoading"
                                @click="toggleKindFilter('new_furnizor')">
                                Furnizori noi: {{ newFurnizorCount }}
                            </v-chip>
                            <v-chip variant="tonal">
                                Neschimbate: {{ result.summary.unchanged }}
                            </v-chip>
                        </div>

                        <v-text-field
                            v-model="searchInput"
                            class="mb-4"
                            label="Caută cod / denumire / furnizor"
                            prepend-inner-icon="mdi-magnify"
                            :variant="fieldVariant"
                            density="compact"
                            hide-details="auto"
                            clearable
                            :disabled="loading"
                            @click:clear="onSearchClear" />
                    </div>

                    <div class="compare-table-shell">
                    <v-data-table
                        :headers="headers"
                        :items="displayedRows"
                        :items-per-page="25"
                        item-value="id"
                        density="compact"
                        class="elevation-1 products-table compare-table"
                        :row-props="compareRowProps">
                        <template v-slot:[`item.serial_number`]="{ item }">
                            <div class="d-flex justify-center">
                                <v-checkbox
                                    :model-value="Boolean(item.serial_number)"
                                    density="compact"
                                    hide-details
                                    color="primary"
                                    class="ma-0 pa-0"
                                    disabled />
                            </div>
                        </template>
                        <template v-slot:[`header.product_code`]="{ column }">
                            <span class="header-title-wrap mr-1">
                                {{ column.title }}
                            </span>
                            <v-icon
                                style="cursor: pointer;"
                                size="small"
                                :color="kindFilter ? 'primary' : undefined"
                                class="fas fa-chevron-down">
                                <v-menu
                                    activator="parent"
                                    :close-on-content-click="true"
                                    class="table-dropdown-menu">
                                    <v-card variant="outlined" style="min-width: 220px;">
                                        <v-list density="compact">
                                            <v-list-item
                                                :active="!kindFilter"
                                                active-class="active-item"
                                                @click="setKindFilter(null)">
                                                Toate
                                            </v-list-item>
                                            <v-divider />
                                            <v-list-item
                                                :active="kindFilter === 'new'"
                                                active-class="active-item"
                                                @click="setKindFilter('new')">
                                                Produs nou
                                            </v-list-item>
                                            <v-list-item
                                                :active="kindFilter === 'new_furnizor'"
                                                active-class="active-item"
                                                @click="setKindFilter('new_furnizor')">
                                                Furnizor nou
                                            </v-list-item>
                                            <v-list-item
                                                :active="kindFilter === 'update'"
                                                active-class="active-item"
                                                @click="setKindFilter('update')">
                                                Actualizare
                                            </v-list-item>
                                            <v-list-item
                                                :active="kindFilter === 'removed'"
                                                active-class="active-item"
                                                @click="setKindFilter('removed')">
                                                Eliminat
                                            </v-list-item>
                                        </v-list>
                                    </v-card>
                                </v-menu>
                            </v-icon>
                        </template>
                        <template v-slot:[`item.product_code`]="{ item }">
                            <div
                                class="d-flex flex-column align-center ga-1 py-1 draft-product-code-cell">
                                <v-chip
                                    size="x-small"
                                    variant="tonal"
                                    :color="kindChipColor(item.draft_kind)"
                                    :prepend-icon="kindChipIcon(item.draft_kind)">
                                    {{ kindLabel(item.draft_kind) }}
                                </v-chip>
                                <span v-if="item.manufacturer_product_code">
                                    {{ item.product_code }}
                                    <v-tooltip activator="parent" location="top">
                                        {{ item.manufacturer_product_code }}
                                    </v-tooltip>
                                </span>
                                <span v-else>{{ item.product_code }}</span>
                            </div>
                        </template>
                        <template v-slot:[`item.product_name`]="{ item }">
                            <span v-if="item.manufacturer_product_name">
                                {{ item.product_name }}
                                <v-tooltip activator="parent" location="top">
                                    {{ item.manufacturer_product_name }}
                                </v-tooltip>
                            </span>
                            <span v-else>{{ item.product_name || '—' }}</span>
                        </template>
                        <template v-slot:[`item.furnizor`]="{ item }">
                            <span v-if="item.furnizor">{{ item.furnizor }}</span>
                            <span v-else class="text-medium-emphasis">—</span>
                        </template>
                        <template v-slot:[`item.categories`]="{ item }">
                            <div
                                v-if="getProductMainCategories(item).length"
                                class="d-flex flex-wrap ga-1 py-1 justify-center">
                                <v-chip
                                    v-for="categoryName in getProductMainCategories(item)"
                                    :key="categoryName"
                                    size="x-small"
                                    variant="flat"
                                    :color="getCategoryColor(categoryName)"
                                    style="color: white">
                                    {{ getCategoryShortLabel(categoryName) }}
                                </v-chip>
                            </div>
                            <span v-else class="text-medium-emphasis">—</span>
                        </template>
                        <template v-slot:[`item.measurement_unit`]="{ item }">
                            <div class="cell-center">{{ item.measurement_unit || '—' }}</div>
                        </template>
                        <template v-slot:[`item.supplier_list_price_old`]="{ item }">
                            {{ formatRonPrice(item.supplier_list_price_old, 2, item.currency) }}
                        </template>
                        <template v-slot:[`item.supplier_list_price_new`]="{ item }">
                            {{ formatRonPrice(item.supplier_list_price_new, 2, item.currency) }}
                        </template>
                        <template v-slot:[`item.supplier_list_price_adjustment_ratio`]="{ item }">
                            <span :class="majorareDiffClass(item.supplier_list_price_adjustment_ratio)">
                                {{ formatPercentOrDash(item.supplier_list_price_adjustment_ratio) }}
                            </span>
                        </template>
                        <template v-slot:[`item.entry_price_ron_inc_vat_old`]="{ item }">
                            {{ formatRonPrice(item.entry_price_ron_inc_vat_old, 2, true) }}
                        </template>
                        <template v-slot:[`item.entry_price_ron_inc_vat`]="{ item }">
                            {{ formatRonPrice(item.entry_price_ron_inc_vat, 2, true) }}
                        </template>
                        <template v-slot:[`item.entry_price_adjustment_ratio`]="{ item }">
                            <span :class="majorareDiffClass(item.entry_price_adjustment_ratio)">
                                {{ formatPercentOrDash(item.entry_price_adjustment_ratio) }}
                            </span>
                        </template>
                        <template v-slot:[`item.markup_reference`]="{ item }">
                            {{ formatPercentOrDash(item.markup_reference) }}
                        </template>
                        <template v-slot:[`item.markup_actual`]="{ item }">
                            {{ formatPercentOrDash(item.markup_actual) }}
                        </template>
                        <template v-slot:[`item.previous_price`]="{ item }">
                            {{ formatRonPrice(item.previous_price, 2, true) }}
                        </template>
                        <template v-slot:[`item.price`]="{ item }">
                            {{ formatRonPrice(item.price, 2, true) }}
                        </template>
                        <template v-slot:[`item.price_adjustment_ratio`]="{ item }">
                            <span :class="majorareDiffClass(item.price_adjustment_ratio)">
                                {{ formatPercentOrDash(item.price_adjustment_ratio) }}
                            </span>
                        </template>
                        <template v-slot:[`item.notes`]="{ item }">
                            <v-tooltip v-if="item.notes" location="top">
                                <template #activator="{ props }">
                                    <v-icon
                                        v-bind="props"
                                        size="small"
                                        color="primary">
                                        mdi-note-text-outline
                                    </v-icon>
                                </template>
                                <div
                                    style="max-width: 320px; white-space: pre-wrap; word-break: break-word;">
                                    {{ item.notes }}
                                </div>
                            </v-tooltip>
                            <span v-else>—</span>
                        </template>
                        <template v-slot:[`item.rowActions`]="{ item }">
                            <div class="draft-row-actions">
                                <v-btn
                                    icon
                                    variant="text"
                                    size="small"
                                    @click="viewProduct(item)">
                                    <v-icon color="primary" size="small">
                                        mdi-eye
                                    </v-icon>
                                    <v-tooltip activator="parent" location="top">
                                        Vezi produs (fără editare)
                                    </v-tooltip>
                                </v-btn>
                            </div>
                        </template>
                    </v-data-table>
                    </div>
                </div>
            </v-card-text>

            <v-card-actions class="pa-4 compare-dialog-actions">
                <v-spacer />
                <v-btn color="danger" variant="elevated" @click="close">
                    Închide
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import {
    getCategoryColor,
    getCategoryShortLabel,
    getProductMainCategories,
} from '@/data/productCategories';
import { comparePriceLists } from '@/utils/priceListApi';
import {
    buildCompareDisplayRows,
    comparePayloadToEditItem,
} from '@/utils/priceListCompareRows';
import { formatPercentValue } from '@/utils/priceListProductCalculator';
import { formatRonPrice, priceListSelectTitle } from '@/utils/utils';
import { includesFolded } from '@/utils/textSearch';

export default {
    name: 'PriceListCompareDialog',
    props: {
        modelValue: { type: Boolean, default: false },
        priceLists: { type: Array, default: () => [] },
    },
    emits: ['update:modelValue', 'view-product'],
    data() {
        return {
            leftId: null,
            rightId: null,
            loading: false,
            filterLoading: false,
            error: '',
            warningDismissed: false,
            result: null,
            searchInput: '',
            search: '',
            statusFilter: null,
            kindFilter: null,
            displayedRows: [],
            tableRows: [],
            searchDebounceTimer: null,
            bootstrappingSelection: false,
            compareRequestId: 0,
            compareAbortController: null,
            lastComparedPairKey: null,
            headers: [
                { title: 'Serial nr.', key: 'serial_number', align: 'center', sortable: true },
                { title: 'Cod produs', key: 'product_code', align: 'center', sortable: true },
                { title: 'Denumire comercială', key: 'product_name', align: 'center', sortable: true },
                { title: 'Furnizor', key: 'furnizor', align: 'center', sortable: true },
                { title: 'Sisteme', key: 'categories', align: 'center', sortable: false },
                { title: 'U.M.', key: 'measurement_unit', align: 'center', sortable: true },
                {
                    title: 'Preț listă vechi furnizor',
                    key: 'supplier_list_price_old',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Preț listă nou furnizor',
                    key: 'supplier_list_price_new',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Diferență',
                    key: 'supplier_list_price_adjustment_ratio',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Preț achiziție vechi fără TVA',
                    key: 'entry_price_ron_inc_vat_old',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Preț achiziție nou fără TVA',
                    key: 'entry_price_ron_inc_vat',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Majorare achiziție',
                    key: 'entry_price_adjustment_ratio',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Adaos referință',
                    key: 'markup_reference',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Adaos actual',
                    key: 'markup_actual',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Preț vânzare vechi cu TVA',
                    key: 'previous_price',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Preț vânzare nou cu TVA',
                    key: 'price',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Diferență',
                    key: 'price_adjustment_ratio',
                    align: 'center',
                    sortable: true,
                    headerProps: { class: 'header-wrap' },
                },
                { title: 'Note', key: 'notes', align: 'center', sortable: false },
                {
                    title: 'Acțiuni',
                    key: 'rowActions',
                    sortable: false,
                    align: 'center',
                    headerProps: { class: 'header-nowrap' },
                    cellProps: { class: 'actions-cell' },
                },
            ],
            priceListItemTitle: priceListSelectTitle,
        };
    },
    computed: {
        fieldVariant() {
            return this.$store?.state?.vuetifyFieldType || 'outlined';
        },
        sortedPriceLists() {
            return [...(this.priceLists || [])].sort((a, b) => {
                const da = a.date_applied || '';
                const db = b.date_applied || '';
                if (da !== db) {
                    return da.localeCompare(db);
                }
                return Number(a.id) - Number(b.id);
            });
        },
        canCompare() {
            if (!this.leftId || !this.rightId || this.leftId === this.rightId) {
                return false;
            }
            const left = this.findPriceListById(this.leftId);
            const right = this.findPriceListById(this.rightId);
            if (!left || !right) {
                return false;
            }
            return this.isSameOrLaterPriceList(right, left);
        },
        tableLoading() {
            return this.loading || this.filterLoading;
        },
        newFurnizorCount() {
            return (this.tableRows || []).filter(
                (row) => row.draft_kind === 'new_furnizor',
            ).length;
        },
        changedCount() {
            return (this.tableRows || []).filter(
                (row) => row.status === 'changed',
            ).length;
        },
        missingSnapshotsWarning() {
            if (!this.result) return '';
            const leftReconstructed =
                this.result.left?.reconstructed_count ?? 0;
            const rightReconstructed =
                this.result.right?.reconstructed_count ?? 0;
            if (leftReconstructed > 0 || rightReconstructed > 0) {
                return (
                    'Pentru produsele fără istoric salvat, comparația folosește prezența în listă '
                    + 'și prețul din listă (date istorice). Alte câmpuri apar în diferențe doar când '
                    + 'ambele liste au istoric salvat; altfel se afișează datele actuale ale produsului.'
                );
            }
            return '';
        },
    },
    watch: {
        modelValue(isOpen) {
            if (isOpen) {
                this.bootstrapSelection();
            } else {
                this.abortInFlightCompare();
                this.clearSearchDebounce();
                this.compareRequestId += 1;
                this.lastComparedPairKey = null;
            }
        },
        priceLists() {
            // Parent refreshes often; only re-bootstrap if dialog is open and
            // current selection is no longer valid — never auto-run compare.
            if (!this.modelValue || this.bootstrappingSelection) {
                return;
            }
            const leftOk = this.leftId == null
                || this.findPriceListById(this.leftId);
            const rightOk = this.rightId == null
                || this.findPriceListById(this.rightId);
            if (!leftOk || !rightOk) {
                this.bootstrapSelection();
            }
        },
        leftId() {
            if (this.bootstrappingSelection) {
                return;
            }
            this.ensureValidRightSelection();
            this.onSelectionChanged();
        },
        rightId() {
            if (this.bootstrappingSelection) {
                return;
            }
            this.onSelectionChanged();
        },
        searchInput(value) {
            if (!this.result || this.loading) {
                return;
            }
            this.clearSearchDebounce();
            this.filterLoading = true;
            this.searchDebounceTimer = setTimeout(() => {
                this.search = value || '';
                this.refreshDisplayedRows().finally(() => {
                    this.filterLoading = false;
                });
            }, 250);
        },
    },
    beforeUnmount() {
        this.abortInFlightCompare();
        this.clearSearchDebounce();
    },
    methods: {
        formatRonPrice,
        getCategoryColor,
        getCategoryShortLabel,
        getProductMainCategories,
        findPriceListById(id) {
            if (id == null) return null;
            return (this.priceLists || []).find(
                (list) => String(list.id) === String(id),
            ) || null;
        },
        /**
         * True when candidate is same day or later than reference (by date_applied).
         * Missing dates sort as earliest.
         */
        isSameOrLaterPriceList(candidate, reference) {
            const candidateDate = String(candidate?.date_applied || '');
            const referenceDate = String(reference?.date_applied || '');
            return candidateDate.localeCompare(referenceDate) >= 0;
        },
        leftItemProps(item) {
            const left = item?.raw ?? item;
            if (!left) {
                return {};
            }
            const hasValidRight = this.sortedPriceLists.some((right) => (
                right.id !== left.id
                && this.isSameOrLaterPriceList(right, left)
            ));
            return { disabled: !hasValidRight };
        },
        rightItemProps(item) {
            const right = item?.raw ?? item;
            if (!right || this.leftId == null) {
                return {};
            }
            const left = this.findPriceListById(this.leftId);
            if (!left) {
                return {};
            }
            if (String(right.id) === String(left.id)) {
                return { disabled: true };
            }
            return { disabled: !this.isSameOrLaterPriceList(right, left) };
        },
        ensureValidRightSelection() {
            if (this.rightId == null) {
                return;
            }
            const left = this.findPriceListById(this.leftId);
            const right = this.findPriceListById(this.rightId);
            if (
                !left
                || !right
                || String(left.id) === String(right.id)
                || !this.isSameOrLaterPriceList(right, left)
            ) {
                this.rightId = null;
            }
        },
        clearCompareResults() {
            this.result = null;
            this.tableRows = [];
            this.displayedRows = [];
            this.error = '';
            this.warningDismissed = false;
            this.statusFilter = null;
            this.kindFilter = null;
            this.searchInput = '';
            this.search = '';
            this.filterLoading = false;
            this.clearSearchDebounce();
            this.lastComparedPairKey = null;
        },
        comparePairKey(leftId = this.leftId, rightId = this.rightId) {
            if (leftId == null || rightId == null) {
                return null;
            }
            return `${leftId}:${rightId}`;
        },
        abortInFlightCompare() {
            if (this.compareAbortController) {
                this.compareAbortController.abort();
                this.compareAbortController = null;
            }
        },
        onSelectionChanged() {
            if (!this.modelValue) {
                return;
            }
            // Selection changed: drop stale results. User must click Compară.
            this.abortInFlightCompare();
            this.clearCompareResults();
        },
        clearSearchDebounce() {
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
                this.searchDebounceTimer = null;
            }
        },
        buildFilteredRows() {
            let rows = this.tableRows;
            if (this.statusFilter) {
                rows = rows.filter((row) => row.status === this.statusFilter);
            }
            if (this.kindFilter) {
                rows = rows.filter((row) => row.draft_kind === this.kindFilter);
            }
            const term = (this.search || '').trim();
            if (!term) return rows;
            return rows.filter((row) => includesFolded(
                [row.product_code, row.product_name, row.furnizor].join(' '),
                term,
            ));
        },
        async refreshDisplayedRows() {
            await new Promise((resolve) => setTimeout(resolve, 30));
            this.displayedRows = this.buildFilteredRows();
        },
        bootstrapSelection() {
            this.bootstrappingSelection = true;
            this.abortInFlightCompare();
            this.clearCompareResults();

            const lists = this.sortedPriceLists;
            let leftId = null;
            let rightId = null;

            if (lists.length >= 2) {
                leftId = lists[lists.length - 2].id;
                rightId = lists[lists.length - 1].id;
            } else if (lists.length === 1) {
                leftId = lists[0].id;
            }

            this.leftId = leftId;
            this.rightId = rightId;

            const left = this.findPriceListById(this.leftId);
            const right = this.findPriceListById(this.rightId);
            if (
                left
                && right
                && (
                    String(this.leftId) === String(this.rightId)
                    || !this.isSameOrLaterPriceList(right, left)
                )
            ) {
                if (lists.length >= 2) {
                    this.leftId = lists[lists.length - 2].id;
                    this.rightId = lists[lists.length - 1].id;
                } else {
                    this.rightId = null;
                }
            }

            this.ensureValidRightSelection();
            this.bootstrappingSelection = false;
        },
        close() {
            this.$emit('update:modelValue', false);
        },
        async runCompare({ force = false } = {}) {
            if (!this.canCompare) return;

            const leftId = this.leftId;
            const rightId = this.rightId;
            const pairKey = this.comparePairKey(leftId, rightId);
            if (
                !force
                && pairKey
                && pairKey === this.lastComparedPairKey
                && this.result
                && !this.loading
            ) {
                return;
            }

            this.abortInFlightCompare();

            const requestId = ++this.compareRequestId;
            const controller = (
                typeof AbortController !== 'undefined'
                    ? new AbortController()
                    : null
            );
            this.compareAbortController = controller;

            this.loading = true;
            this.error = '';
            this.warningDismissed = false;
            this.filterLoading = false;
            this.clearSearchDebounce();
            this.statusFilter = null;
            this.kindFilter = null;
            this.searchInput = '';
            this.search = '';
            this.displayedRows = [];
            this.tableRows = [];
            try {
                const result = await comparePriceLists(leftId, rightId, {
                    signal: controller?.signal,
                });
                if (requestId !== this.compareRequestId) {
                    return;
                }
                this.result = result;
                this.lastComparedPairKey = pairKey;
                this.tableRows = buildCompareDisplayRows(this.result);
                await this.refreshDisplayedRows();
            } catch (err) {
                if (
                    requestId !== this.compareRequestId
                    || err?.code === 'ERR_CANCELED'
                    || err?.name === 'CanceledError'
                    || err?.name === 'AbortError'
                ) {
                    return;
                }
                const detail =
                    err?.response?.data?.detail
                    || err?.message
                    || 'Compararea listelor a eșuat.';
                this.error = detail;
                this.result = null;
                this.lastComparedPairKey = null;
                this.displayedRows = [];
                this.tableRows = [];
            } finally {
                if (requestId === this.compareRequestId) {
                    this.loading = false;
                    if (this.compareAbortController === controller) {
                        this.compareAbortController = null;
                    }
                }
            }
        },
        async toggleStatusFilter(status) {
            if (this.tableLoading) return;
            this.filterLoading = true;
            await this.$nextTick();
            this.statusFilter = this.statusFilter === status ? null : status;
            if (this.statusFilter) {
                this.kindFilter = null;
            }
            try {
                await this.refreshDisplayedRows();
            } finally {
                this.filterLoading = false;
            }
        },
        async toggleKindFilter(kind) {
            if (this.tableLoading) return;
            this.filterLoading = true;
            await this.$nextTick();
            this.kindFilter = this.kindFilter === kind ? null : kind;
            if (this.kindFilter) {
                this.statusFilter = null;
            }
            try {
                await this.refreshDisplayedRows();
            } finally {
                this.filterLoading = false;
            }
        },
        async setKindFilter(kind) {
            this.filterLoading = true;
            this.kindFilter = kind;
            if (kind) {
                this.statusFilter = null;
            }
            try {
                await this.refreshDisplayedRows();
            } finally {
                this.filterLoading = false;
            }
        },
        async onSearchClear() {
            this.clearSearchDebounce();
            this.filterLoading = true;
            this.searchInput = '';
            this.search = '';
            try {
                await this.refreshDisplayedRows();
            } finally {
                this.filterLoading = false;
            }
        },
        kindLabel(kind) {
            if (kind === 'new') return 'Nou';
            if (kind === 'new_furnizor') return 'Furnizor nou';
            if (kind === 'removed' || kind === 'removed_furnizor') return 'Eliminat';
            return 'Actualizare';
        },
        kindChipColor(kind) {
            if (kind === 'new') return 'success';
            if (kind === 'new_furnizor') return 'info';
            if (kind === 'removed' || kind === 'removed_furnizor') return 'error';
            return 'default';
        },
        kindChipIcon(kind) {
            if (kind === 'new') return 'mdi-plus-circle-outline';
            if (kind === 'new_furnizor') return 'mdi-truck-plus-outline';
            if (kind === 'removed' || kind === 'removed_furnizor') {
                return 'mdi-minus-circle-outline';
            }
            return 'mdi-pencil-outline';
        },
        compareRowProps({ item }) {
            if (item?.draft_kind === 'new') {
                return { class: 'draft-row--new' };
            }
            if (item?.draft_kind === 'new_furnizor') {
                return { class: 'compare-row--new-furnizor' };
            }
            if (
                item?.draft_kind === 'removed'
                || item?.draft_kind === 'removed_furnizor'
            ) {
                return { class: 'compare-row--removed' };
            }
            return {};
        },
        formatPercentOrDash(value) {
            if (value == null || value === '') return '—';
            const num = Number(value);
            if (!Number.isFinite(num)) return '—';
            return formatPercentValue(num);
        },
        majorareDiffClass(value) {
            if (value == null || value === '') return '';
            const num = Number(value);
            if (!Number.isFinite(num) || num === 0) return '';
            return num > 0 ? 'text-error' : 'text-success';
        },
        viewProduct(item) {
            const payload = item?.right_payload
                || item?.payload
                || item?.left_payload
                || null;
            const editItem = comparePayloadToEditItem(payload);
            if (!editItem) {
                return;
            }
            this.$emit('view-product', {
                editItem,
                changes: item.changes || [],
                compareKind: item.draft_kind,
                furnizorTabKey: item.furnizorTabKey,
                // Left list only — used for „Valoare veche” in read-only compare view.
                // Skip for removed rows (we're already viewing the reference list).
                referencePayload:
                    item.status === 'removed'
                        ? null
                        : (item.left_payload || null),
            });
        },
    },
};
</script>

<style scoped>
.compare-dialog-card {
    display: flex;
    flex-direction: column;
    max-height: 90vh;
    overflow: hidden;
}

.compare-dialog-card--with-results {
    height: 90vh;
}

.compare-dialog-toolbar,
.compare-dialog-actions {
    flex: 0 0 auto;
}

.compare-dialog-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.compare-dialog-controls,
.compare-results-toolbar {
    flex: 0 0 auto;
}

.compare-results {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.compare-table-shell {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.compare-table {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    display: flex !important;
    flex-direction: column;
    overflow: hidden;
}

.compare-table :deep(.v-table) {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.compare-table :deep(.v-table__wrapper) {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    height: auto !important;
    max-height: none !important;
    overflow-y: auto !important;
}

.compare-table :deep(thead th) {
    position: sticky;
    top: 0;
    z-index: 3;
    background: rgb(var(--v-theme-surface));
}

.compare-table :deep(.v-data-table-footer) {
    flex: 0 0 auto;
    background: rgb(var(--v-theme-surface));
    border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.compare-results-overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-radius: 4px;
}

.compare-snapshot-warning {
    --v-theme-on-warning: 120, 72, 0;
    color: #784800 !important;
}

.compare-snapshot-warning :deep(.v-alert__content),
.compare-snapshot-warning :deep(.v-icon),
.compare-snapshot-warning :deep(.v-btn) {
    color: #784800 !important;
    opacity: 1;
}

.header-title-wrap {
    white-space: normal;
    line-height: 1.2;
}

.cell-center {
    text-align: center;
}

.draft-row-actions {
    display: flex;
    justify-content: center;
    gap: 2px;
}

:deep(.draft-row--new) {
    background-color: rgba(76, 175, 80, 0.06);
}

:deep(.compare-row--new-furnizor) {
    background-color: rgba(33, 150, 243, 0.06);
}

:deep(.compare-row--removed) {
    background-color: rgba(244, 67, 54, 0.06);
}

:deep(.header-wrap) {
    white-space: normal !important;
    line-height: 1.2;
}
</style>

<style>
.v-dialog--fullscreen .compare-dialog-card,
.v-dialog--fullscreen .compare-dialog-card--with-results {
    height: 100% !important;
    max-height: 100% !important;
}
</style>
